import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { PROJECT_FILES_QUERY } from '../../graphql/queries';
import styles from './FileViewer.module.css';
import Icon, { type IconName } from '../../components/common/Icon';

type ViewerType = 'viewer-2d' | 'viewer-3d' | 'viewer-pdf' | 'viewer-cloud' | 'viewer-indoor';

const VIEWER_CONFIG: Record<ViewerType, { title: string; icon: IconName; placeholder: string; tools: IconName[] }> = {
  'viewer-2d': {
    title: '2D Drawing Viewer',
    icon: 'architecture',
    placeholder: 'DWG/CAD Viewer — Integration with OpenDesign or Autodesk Viewer SDK required',
    tools: ['pan_tool', 'zoom_in', 'zoom_out', 'fit_screen', 'straighten', 'layers', 'square_foot'],
  },
  'viewer-3d': {
    title: '3D BIM Viewer',
    icon: 'view_in_ar',
    placeholder: 'IFC/RVT 3D Viewer — Integration with IFC.js or BIM Viewer SDK required',
    tools: ['pan_tool', 'zoom_in', 'zoom_out', 'fit_screen', '3d_rotation', 'view_in_ar', 'layers'],
  },
  'viewer-pdf': {
    title: 'PDF Document Viewer',
    icon: 'description',
    placeholder: 'PDF Viewer — Integration with PDF.js required',
    tools: ['zoom_in', 'zoom_out', 'fit_screen', 'rotate_right', 'first_page', 'last_page'],
  },
  'viewer-cloud': {
    title: 'Point Cloud Viewer',
    icon: 'grain',
    placeholder: 'E57/LGSX/RCP Point Cloud Viewer — Integration with Potree or LAStools SDK required',
    tools: ['pan_tool', 'zoom_in', 'zoom_out', 'fit_screen', '3d_rotation', 'grain', 'palette'],
  },
  'viewer-indoor': {
    title: 'Indoor Viewer',
    icon: 'location_city',
    placeholder: 'Indoor Floor Plan Viewer — Integration with floor plan rendering library required',
    tools: ['pan_tool', 'zoom_in', 'zoom_out', 'fit_screen', 'layers', 'straighten'],
  },
};

const FileViewer = () => {
  const { projectId, viewerType, fileId } = useParams<{ projectId: string; viewerType: string; fileId: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  if (!isAuthenticated) return <Navigate to="/login" replace />;

  const config = VIEWER_CONFIG[viewerType as ViewerType];
  if (!config) return <Navigate to={`/projects/${projectId}`} replace />;

  return (
    <div className={styles.viewerContainer}>
      <header className={styles.viewerHeader}>
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className={styles.toolButton}
          >
            <Icon name="arrow_back" />
            <span>Back</span>
          </button>
          <div className="w-px h-5 bg-white/20" />
          <div className="flex items-center gap-2">
            <Icon name={config.icon} className="text-white/60 text-lg" />
            <h1 className="text-sm font-semibold text-white">{config.title}</h1>
          </div>
        </div>

        <div className={styles.toolbar}>
          {config.tools.map((tool) => (
            <button key={tool} className={styles.toolButton} title={tool.replace(/_/g, ' ')}>
              <Icon name={tool} className="text-lg" />
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <button className={styles.toolButton}>
            <Icon name="share" className="text-lg" />
            <span>Share</span>
          </button>
          <button className={styles.toolButton}>
            <Icon name="download" className="text-lg" />
            <span>Download</span>
          </button>
          <button className={styles.toolButton} aria-label="Full screen">
            <Icon name="fullscreen" className="text-lg" />
          </button>
        </div>
      </header>

      <div className={styles.viewerMain}>
        <div className={styles.viewerCanvas}>
          <div className={styles.placeholder}>
            <div className="w-24 h-24 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center mx-auto mb-6">
              <Icon name={config.icon} className="text-5xl text-white/30" />
            </div>
            <p className="text-lg font-semibold text-white/60 mb-2">{config.title}</p>
            <p className="text-sm text-white/30 max-w-md mx-auto">{config.placeholder}</p>
            <div className="mt-8 p-4 bg-white/5 rounded-xl border border-white/10 inline-block text-left">
              <p className="text-[10px] uppercase tracking-widest text-white/40 mb-2">File ID</p>
              <p className="text-sm font-mono text-white/60">{fileId || 'No file selected'}</p>
            </div>
          </div>
        </div>

        <aside className={styles.infoPanel}>
          <h3 className="text-sm font-bold text-white mb-4 uppercase tracking-wider">File Info</h3>
          <div className="space-y-4">
            {[
              { label: 'Project', value: projectId ? `#${projectId.slice(-6)}` : '—' },
              { label: 'File ID', value: fileId ? `#${fileId.slice(-6)}` : '—' },
              { label: 'Viewer', value: config.title },
            ].map((info) => (
              <div key={info.label}>
                <p className="text-[10px] uppercase tracking-widest text-white/40 mb-1">{info.label}</p>
                <p className="text-sm text-white/70 font-mono">{info.value}</p>
              </div>
            ))}
          </div>

          <div className="mt-8">
            <h3 className="text-sm font-bold text-white mb-4 uppercase tracking-wider">Navigation</h3>
            <div className="space-y-2">
              {([
                { icon: 'zoom_in', label: 'Zoom In', shortcut: '+' },
                { icon: 'zoom_out', label: 'Zoom Out', shortcut: '-' },
                { icon: 'fit_screen', label: 'Fit to Screen', shortcut: 'F' },
                { icon: 'pan_tool', label: 'Pan Mode', shortcut: 'P' },
              ] as const).map((shortcut) => (
                <div key={shortcut.label} className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2 text-white/50">
                    <Icon name={shortcut.icon} className="text-sm" />
                    {shortcut.label}
                  </div>
                  <kbd className="px-1.5 py-0.5 bg-white/10 rounded text-white/40 font-mono text-[10px]">{shortcut.shortcut}</kbd>
                </div>
              ))}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default FileViewer;
